import { db } from '../../db';
import { syncService } from '../../services/SyncService';

const timestamp = () => new Date().toISOString();
const generateUUID = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
  ? crypto.randomUUID()
  : `id-${Math.random().toString(36).substring(2, 11)}`;

export async function createDraft({ appointmentId, templateVersionId, authorId, correctionAssessment = null }) {
  const existing = await loadDraft({
    appointmentId, templateVersionId, authorId,
    kind: correctionAssessment ? 'correction' : 'initial',
    assessmentId: correctionAssessment?.id,
  });
  if (existing) return existing;
  const [version, appointment] = await Promise.all([
    db.form_template_versions.get(templateVersionId), db.appointments.get(appointmentId),
  ]);
  if (!version || !appointment) throw new Error('Assessment template or appointment is unavailable');
  const now = timestamp();
  const draft = {
    id: generateUUID(), appointment_id: appointmentId, template_version_id: templateVersionId,
    template_id: version.template_id, clinic_id: appointment.clinic_id || null,
    assessment_id: correctionAssessment?.id || generateUUID(), revision_id: generateUUID(),
    author_id: authorId, kind: correctionAssessment ? 'correction' : 'initial',
    base_revision_number: correctionAssessment?.latest_revision_number || 0,
    answers: correctionAssessment?.answers || {}, correction_reason: '', state: 'editing', sync_error: null,
    created_at: now, updated_at: now,
  };
  await db.form_assessment_drafts.add(draft);
  return draft;
}

export async function loadDraft({ appointmentId, templateVersionId, authorId, kind, assessmentId = null }) {
  const all = (await db.form_assessment_drafts.toArray()) || [];
  return all.find(item =>
    item.appointment_id === appointmentId && item.template_version_id === templateVersionId
    && item.author_id === authorId && item.kind === kind
    && (kind !== 'correction' || item.assessment_id === assessmentId)
  ) || null;
}

export async function saveDraft(draft) {
  const saved = { ...draft, updated_at: timestamp() };
  await db.form_assessment_drafts.put(saved);
  return saved;
}

export async function discardDraft(draftId) {
  await db.transaction('rw', db.form_assessment_drafts, db.form_assessments, db.form_assessment_revisions, db.sync_queue, async () => {
    const draft = await db.form_assessment_drafts.get(draftId);
    if (!draft) return;

    const queueItems = await db.sync_queue.toArray();
    await Promise.all(queueItems
      .filter(item => item.data?.draft_id === draftId)
      .map(item => db.sync_queue.delete(item.id)));

    const assessment = await db.form_assessments.get(draft.assessment_id);
    if (draft.kind === 'correction' && assessment) {
      const prior = draft.prior_assessment_state || {
        status: 'submitted', sync_status: 'synced', sync_error: null, _synced: true,
      };
      await db.form_assessments.update(draft.assessment_id, {
        latest_revision_number: draft.base_revision_number,
        status: prior.status,
        sync_status: prior.sync_status,
        sync_error: prior.sync_error,
        _synced: prior._synced,
        ...(prior.updated_at ? { updated_at: prior.updated_at } : {}),
      });
    } else if (assessment && assessment._synced !== true) {
      await db.form_assessments.delete(draft.assessment_id);
    }

    const revision = await db.form_assessment_revisions.get(draft.revision_id);
    if (revision && revision._synced !== true) await db.form_assessment_revisions.delete(draft.revision_id);

    await db.form_assessment_drafts.delete(draftId);
  });
}

const queueRecord = draft => {
  if (draft.kind === 'correction') {
    return {
      type: 'create', entity: 'form_assessment_correction', entity_id: draft.assessment_id,
      data: {
        assessment_id: draft.assessment_id, revision_id: draft.revision_id,
        expected_revision_number: draft.base_revision_number + 1, answers: draft.answers,
        correction_reason: draft.correction_reason, draft_id: draft.id,
      }, status: 'pending', created_at: timestamp(),
    };
  }
  return {
    type: 'create', entity: 'form_assessment_submit', entity_id: draft.assessment_id,
    data: {
      id: draft.assessment_id, revision_id: draft.revision_id, appointment_id: draft.appointment_id,
      template_version_id: draft.template_version_id, answers: draft.answers, draft_id: draft.id,
    }, status: 'pending', created_at: timestamp(),
  };
};

export async function stageDraft(draft) {
  const now = timestamp();
  let stagedDraft;
  await db.transaction('rw', db.form_assessment_drafts, db.form_assessments, db.form_assessment_revisions, db.sync_queue, async () => {
    const existingAssessment = draft.kind === 'correction'
      ? await db.form_assessments.get(draft.assessment_id)
      : null;
    stagedDraft = {
      ...draft, state: 'pending', sync_error: null, updated_at: now,
      ...(draft.prior_assessment_state === undefined && existingAssessment ? {
        prior_assessment_state: {
          status: existingAssessment.status,
          sync_status: existingAssessment.sync_status,
          sync_error: existingAssessment.sync_error,
          _synced: existingAssessment._synced,
          updated_at: existingAssessment.updated_at,
        },
      } : {}),
    };
    await db.form_assessment_drafts.put(stagedDraft);
    if (draft.kind === 'correction') {
      await db.form_assessments.update(draft.assessment_id, {
        latest_revision_number: draft.base_revision_number + 1,
        status: 'submitted', sync_status: 'pending', sync_error: null, updated_at: now,
      });
    } else {
      await db.form_assessments.put({
        id: draft.assessment_id, appointment_id: draft.appointment_id, template_id: draft.template_id,
        template_version_id: draft.template_version_id, author_id: draft.author_id, clinic_id: draft.clinic_id,
        latest_revision_number: draft.base_revision_number + 1,
        status: 'submitted', sync_status: 'pending', sync_error: null, _synced: false, created_at: draft.created_at, updated_at: now,
      });
    }
    await db.form_assessment_revisions.put({
      id: draft.revision_id, assessment_id: draft.assessment_id, revision_number: draft.base_revision_number + 1,
      answers: draft.answers, submitted_by: draft.author_id,
      correction_reason: draft.kind === 'correction' ? draft.correction_reason : null,
      submitted_at: now, created_at: now, _synced: false,
    });
    await db.sync_queue.add(queueRecord(draft));
  });
  syncService.processQueue();
  return stagedDraft;
}

export async function retryDraft(draftId) {
  const draft = await db.form_assessment_drafts.get(draftId);
  if (!draft) throw new Error('Assessment draft not found');
  await db.transaction('rw', db.form_assessment_drafts, db.form_assessments, db.sync_queue, async () => {
    await db.form_assessment_drafts.update(draftId, { state: 'pending', sync_error: null, updated_at: timestamp() });
    await db.form_assessments.update(draft.assessment_id, { sync_status: 'pending', sync_error: null, _synced: false });
    await db.sync_queue.add(queueRecord(draft));
  });
  syncService.processQueue();
}

export async function countLocalAssessmentWork() {
  return db.form_assessment_drafts.count();
}
